import { normalizeEmailSignatures } from '../../utils/emailSignatures';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { User, Company, Permission, AuthContext as AuthContextType } from '../../types/auth';
import { Workspace, WorkspaceMembership, WorkspaceRole } from '../../types/workspace';
import { getUserPermissions, canUserAccess, canUserViewCompany, isInternalRole, isAdministratorRole } from '../../utils/permissions';
import { DataService } from '../../services/DataService';
import { supabase, resetSessionExpiredLatch, isDeadSessionError } from '../../utils/supabase/client';
import { clearSession } from '../../utils/cookies';
import { DeviceTokenBridge } from '../../services/DeviceTokenBridge';
import { NativeBridge } from '../../services/NativeBridge';
import { isDemoUser, patchDemoUserCompany } from '../../services/dataService/demoFixture';
import { publishPublicKey as e2ePublishPublicKey } from '../../features/messages/services/e2eKeyStore';
import { clearProjectNotesCache } from '../../services/projectNotesStore';
import { preserveWorkspaceLock, rememberWorkspaceAccessToken, workspaceLockKey } from './workspaceLock';
import { sameAuthUser } from './sameAuthUser';
import { planAuthEventSync } from './authEventSyncPolicy';
import { pendingMfaForSession } from './mfa';
import { setItemWithEviction } from '../../utils/storageQuota';
import {
  classifyBootSession,
  canDrawSavedSession,
  isStoredSessionLive,
  NATIVE_SESSION_BOOT_WAIT_MS,
  peekStoredSession,
  TRANSIENT_RECHECK_DELAYS_MS,
} from './sessionBootstrapPolicy';

interface WorkspaceMembershipRow {
  id?: string;
  workspace_id: string;
  user_id: string;
  workspace_role: WorkspaceRole;
  permissions: unknown;
  status: 'active' | 'inactive' | 'invited' | 'suspended';
  is_primary: boolean;
  metadata?: Record<string, unknown> | null;
}

interface WorkspaceRow {
  id: string;
  name: string;
  slug?: string | null;
  type: 'internal' | 'organisation';
  status: 'active' | 'inactive';
}

interface WorkspaceCompanyRow {
  id: string;
  name: string;
  workspace_id: string | null;
}

function isValidUUID(uuid: string): boolean {
  const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return regex.test(uuid);
}

function getStoredUser(): User | null {
  try {
    const savedUser = localStorage.getItem('auth_user');
    if (!savedUser) return null;

    const userData = JSON.parse(savedUser);
    if (userData && userData.id && !isValidUUID(userData.id)) {
      console.warn('Detected legacy user ID format (non-UUID). Clearing session to force re-login.');
      localStorage.removeItem('auth_user');
      clearSession();
      return null;
    }

    return userData;
  } catch (error) {
    console.error('Error loading saved session:', error);
    localStorage.removeItem('auth_user');
    return null;
  }
}

function getUserCompanyId(user: User | null): string | null {
  if (!user) return null;
  return user.company_id || user.companyId || null;
}

function getUserWorkspaceId(user: User | null): string | null {
  if (!user) return null;
  return user.workspace_id || user.workspaceId || null;
}

function getUserActiveWorkspaceId(user: User | null): string | null {
  if (!user) return null;
  return user.active_workspace_id || user.activeWorkspaceId || getUserWorkspaceId(user);
}

function normalizePermissionList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }
  return [];
}

function mergeWorkspaceFields(user: User, workspaceId: string | null): User {
  return {
    ...user,
    activeWorkspaceId: workspaceId,
    active_workspace_id: workspaceId,
  };
}

function normalizeAuthUserRow(row: any): User {
  return {
    ...row,
    companyId: row.companyId || row.company_id || null,
    company_id: row.company_id || row.companyId || null,
    workspaceId: row.workspaceId || row.workspace_id || null,
    workspace_id: row.workspace_id || row.workspaceId || null,
    activeWorkspaceId: row.activeWorkspaceId || row.active_workspace_id || row.workspaceId || row.workspace_id || null,
    active_workspace_id: row.active_workspace_id || row.activeWorkspaceId || row.workspace_id || row.workspaceId || null,
    createdAt: row.createdAt || row.created_at,
    updatedAt: row.updatedAt || row.updated_at,
    firstName: row.firstName || row.first_name || null,
    first_name: row.first_name || row.firstName || null,
    lastName: row.lastName || row.last_name || null,
    last_name: row.last_name || row.lastName || null,
    displayName: row.displayName || row.display_name || row.name || null,
    display_name: row.display_name || row.displayName || row.name || null,
    jobTitle: row.jobTitle || row.job_title || row.position || null,
    job_title: row.job_title || row.jobTitle || row.position || null,
    emailSignatures: normalizeEmailSignatures(row.emailSignatures ?? row.email_signatures),
    lastLogin: row.lastLogin || row.last_login || null,
    last_login: row.last_login || row.lastLogin || null,
    manualPresenceStatus: row.manualPresenceStatus || row.manual_presence_status || null,
    manual_presence_status: row.manual_presence_status || row.manualPresenceStatus || null,
    manualPresenceLabel: row.manualPresenceLabel || row.manual_presence_label || null,
    manual_presence_label: row.manual_presence_label || row.manualPresenceLabel || null,
    manualPresenceUntil: row.manualPresenceUntil || row.manual_presence_until || null,
    manual_presence_until: row.manual_presence_until || row.manualPresenceUntil || null,
    manualPresenceClearMode: row.manualPresenceClearMode || row.manual_presence_clear_mode || null,
    manual_presence_clear_mode: row.manual_presence_clear_mode || row.manualPresenceClearMode || null,
    manualPresenceSetAt: row.manualPresenceSetAt || row.manual_presence_set_at || null,
    manual_presence_set_at: row.manual_presence_set_at || row.manualPresenceSetAt || null,
  } as User;
}

function buildNativeAuthSessionPayload(session: any) {
  if (!session?.access_token || !session?.refresh_token) {
    return null;
  }

  return {
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    expires_at: session.expires_at ?? null,
    expires_in: session.expires_in ?? null,
    user: session.user
      ? {
          id: session.user.id ?? null,
          email: session.user.email ?? null,
        }
      : null,
  };
}

/**
 * Publish the Supabase session tokens to the macOS native shell so it can
 * establish its own authenticated Supabase client (e.g. for native takeoff).
 *
 * Strictly gated to the macOS WKWebView shell — no-op in a browser, Tauri
 * desktop, or iOS. Tokens are never logged.
 *
 * Contract shape consumed by the native receiver:
 *   window.QuantisNativeBridge.send('auth:session', {
 *     accessToken, refreshToken, expiresAt  // expiresAt = unix seconds | null
 *   })
 */
function publishMacAuthSession(session: any): void {
  if (!session?.access_token || !session?.refresh_token) return;
  if (!NativeBridge.isMacShell()) return;
  window.QuantisNativeBridge?.send?.('auth:session', {
    accessToken: session.access_token,
    refreshToken: session.refresh_token,
    expiresAt: session.expires_at ?? null,
  });
}

/**
 * Notify the native shell (iOS, macOS or Android) that the session has ended.
 * Gated to any native shell, not just macOS — iOS and older Android builds
 * ignore an auth:signout they don't handle (see AuthContext.tsx callers).
 */
export function publishNativeAuthSignout(): void {
  if (!NativeBridge.isRunningNatively()) return;
  window.QuantisNativeBridge?.send?.('auth:signout', {});
}

async function getSessionWithTimeout(timeoutMs = 8000) {
  let timeoutId: number | null = null;

  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = window.setTimeout(() => {
        reject(new Error(`Supabase session bootstrap timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    });

    return await Promise.race([
      supabase.auth.getSession(),
      timeoutPromise,
    ]);
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  }
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, _setUser] = useState<User | null>((): User | null => {
    const stored = getStoredUser();
    // Cold reload while demo is signed in: enter demo mode synchronously
    // before the first render so no cached real-user data ever flashes on
    // screen.
    if (stored && isDemoUser(stored)) {
      const patched = patchDemoUserCompany(stored as any); // TODO: tighten — auth.User vs database.User split
      DataService.enterDemoMode(patched as any);
      return patched as any as User;
    }
    return stored;
  });
  // A saved session draws the app on its last-known data at once; boot's
  // check below still runs behind it and signs a dead session out.
  const [status, setStatus] = useState<'unknown' | 'authenticated' | 'unauthenticated'>(() => {
    const stored = getStoredUser();
    if (!stored) return 'unauthenticated';
    return canDrawSavedSession({
      peek: peekStoredSession(),
      savedUserId: stored.id,
      native: NativeBridge.isRunningNatively(),
      nowMs: Date.now(),
    }) ? 'authenticated' : 'unknown';
  });
  const [company, setCompany] = useState<Company | null>(null);
  const [workspaceMemberships, setWorkspaceMemberships] = useState<WorkspaceMembership[]>([]);
  const [availableWorkspaces, setAvailableWorkspaces] = useState<Workspace[]>([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(() => getUserActiveWorkspaceId(getStoredUser()));

  const setUser = (newUser: User | null) => {
    _setUser(prev => {
      if (!prev && !newUser) return prev;
      if (prev === newUser) return prev;
      if (prev && newUser && JSON.stringify(prev) === JSON.stringify(newUser)) return prev;
      return newUser;
    });
  };

  const userRef = useRef<User | null>(user);
  useEffect(() => {
    userRef.current = user;
  });

  const persistAuthUser = useCallback((nextUser: User | null) => {
    if (!nextUser) {
      localStorage.removeItem('auth_user');
      return;
    }
    // A full quota must not abort sign-in half-way: the user is already in
    // React state, and a failed write only costs the cold-reload shortcut.
    setItemWithEviction('auth_user', JSON.stringify(nextUser));
  }, []);

  const activeWorkspace = useMemo(() => {
    if (!activeWorkspaceId) return null;
    return availableWorkspaces.find(workspace => workspace.id === activeWorkspaceId) || null;
  }, [activeWorkspaceId, availableWorkspaces]);

  // Guard against re-entrant logout calls. Supabase fires SIGNED_OUT when we
  // call signOut(), and the listener calls logout() — without this guard that
  // creates an infinite loop.
  const isLoggingOutRef = useRef(false);

  // iOS web shell: guards the one-time app-user hydrate from a native-injected
  // Supabase session, so token-refresh re-injections don't re-fetch the user.
  const nativeUserEstablishedRef = useRef(false);

  const clearLocalAuthState = useCallback(() => {
    DataService.stopRealtimeSubscriptions();
    DataService.exitDemoMode();
    DataService.resetDatabase();
    DeviceTokenBridge.clearUser();
    NativeBridge.setAuthenticated(false);
    NativeBridge.send('authSession', {
      status: 'signed_out',
      authenticated: false,
      isAuthenticated: false,
    });
    // Tell the native (iOS/macOS/Android) Supabase client to invalidate its session.
    publishNativeAuthSignout();
    setUser(null);
    setStatus('unauthenticated');
    setCompany(null);
    setWorkspaceMemberships([]);
    setAvailableWorkspaces([]);
    setActiveWorkspaceId(null);
    localStorage.removeItem('auth_user');
    // Drop the per-user project-list caches (priced BOQ totals + has-BOQ flags)
    // so a different user signing in on this browser can't transiently see the
    // previous user's figures before the background refresh corrects them.
    localStorage.removeItem('quantis-boq-totals-cache-v1');
    localStorage.removeItem('quantis-projects-with-boq-cache-v1');
    clearProjectNotesCache();
    window.dispatchEvent(new Event('quantis:auth-cleared'));
    // Allow the next native-injected session to hydrate a fresh app user.
    nativeUserEstablishedRef.current = false;
    clearSession();
  }, []);

  const logout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    sessionStorage.removeItem(workspaceLockKey);

    clearLocalAuthState();

    try {
      // A normal logout belongs to this browser/device. Supabase defaults to
      // `global`, which would also revoke the user's sessions everywhere else.
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.error('Failed to complete Supabase sign out', error);
    } finally {
      isLoggingOutRef.current = false;
    }

    // Force the SPA back to the public root so stale authenticated route
    // state cannot survive logout on native shells or reused browser sessions.
    if (typeof window !== 'undefined' && window.location.pathname !== '/') {
      window.location.replace('/');
    } else if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }, [clearLocalAuthState]);

  const loadWorkspaceContext = useCallback(async (currentUser: User) => {
    try {
      const { data: membershipRows, error: membershipsError } = await supabase
        .from('workspace_memberships')
        .select('id, workspace_id, user_id, workspace_role, permissions, status, is_primary, metadata')
        .eq('user_id', currentUser.id);

      if (membershipsError) {
        throw membershipsError;
      }

      const normalizedMembershipRows = (membershipRows || []) as WorkspaceMembershipRow[];
      const workspaceIds = Array.from(
        new Set(
          normalizedMembershipRows
            .map(membership => membership.workspace_id)
            .filter((workspaceId): workspaceId is string => Boolean(workspaceId)),
        ),
      );

      if (workspaceIds.length === 0) {
        setWorkspaceMemberships([]);
        setAvailableWorkspaces([]);
        setActiveWorkspaceId(getUserActiveWorkspaceId(currentUser));
        return;
      }

      const [{ data: workspaceRows, error: workspacesError }, { data: companyRows, error: companiesError }] = await Promise.all([
        supabase
          .from('workspaces')
          .select('id, name, slug, type, status')
          .in('id', workspaceIds),
        // Read company identity from workspaces using the alias trick so
        // downstream consumers see the same shape (id = text company id,
        // workspace_id = the workspace uuid). The companies table is legacy;
        // workspaces.legacy_company_id holds the canonical text id.
        supabase
          .from('workspaces')
          .select('id:legacy_company_id, name, workspace_id:id')
          .in('id', workspaceIds),
      ]);

      if (workspacesError) {
        throw workspacesError;
      }
      if (companiesError) {
        throw companiesError;
      }

      const workspaceRowsById = new Map(
        ((workspaceRows || []) as WorkspaceRow[]).map(workspace => [workspace.id, workspace]),
      );
      const companyRowsByWorkspaceId = new Map(
        ((companyRows || []) as WorkspaceCompanyRow[])
          .filter(companyRow => Boolean(companyRow.workspace_id))
          .map(companyRow => [companyRow.workspace_id as string, companyRow]),
      );

      const memberships = (normalizedMembershipRows
        .map(membershipRow => {
          const workspaceRow = workspaceRowsById.get(membershipRow.workspace_id);
          if (!workspaceRow) return null;
          const companyRow = companyRowsByWorkspaceId.get(membershipRow.workspace_id);

          const workspace: Workspace = {
            id: workspaceRow.id,
            name: workspaceRow.name,
            slug: workspaceRow.slug ?? null,
            type: workspaceRow.type,
            status: workspaceRow.status,
            companyId: companyRow?.id ?? null,
            companyName: companyRow?.name ?? null,
          };

          return {
            id: membershipRow.id,
            workspaceId: membershipRow.workspace_id,
            userId: membershipRow.user_id,
            workspaceRole: membershipRow.workspace_role,
            permissions: normalizePermissionList(membershipRow.permissions),
            status: membershipRow.status,
            isPrimary: membershipRow.is_primary,
            metadata: membershipRow.metadata ?? {},
            workspace,
          } satisfies WorkspaceMembership;
        })
        .filter(Boolean)) as WorkspaceMembership[];

      const workspaces = memberships
        .map(membership => ({
          ...membership.workspace!,
          membershipRole: membership.workspaceRole,
          membershipStatus: membership.status,
          isPrimary: membership.isPrimary,
        }))
        .sort((left, right) => {
          if (left.isPrimary !== right.isPrimary) {
            return left.isPrimary ? -1 : 1;
          }
          return left.name.localeCompare(right.name);
        })
        .map(({ membershipRole: _membershipRole, membershipStatus: _membershipStatus, isPrimary: _isPrimary, ...workspace }) => workspace);

      const requestedWorkspaceId = getUserActiveWorkspaceId(currentUser);
      const defaultWorkspaceId =
        memberships.find(membership => membership.isPrimary)?.workspaceId ||
        getUserWorkspaceId(currentUser) ||
        workspaces[0]?.id ||
        null;
      const resolvedActiveWorkspaceId =
        (requestedWorkspaceId && workspaces.some(workspace => workspace.id === requestedWorkspaceId))
          ? requestedWorkspaceId
          : defaultWorkspaceId;

      setWorkspaceMemberships(memberships);
      setAvailableWorkspaces(workspaces);
      setActiveWorkspaceId(resolvedActiveWorkspaceId);

      if (resolvedActiveWorkspaceId !== getUserActiveWorkspaceId(currentUser)) {
        const nextUser = mergeWorkspaceFields(currentUser, resolvedActiveWorkspaceId);
        setUser(nextUser);
        persistAuthUser(nextUser);
      }
    } catch (error) {
      console.error('Error loading workspace memberships:', error);
      setWorkspaceMemberships([]);
      setAvailableWorkspaces([]);
      setActiveWorkspaceId(getUserActiveWorkspaceId(currentUser));
    }
  }, [persistAuthUser]);

  const hydrateUserFromBackend = useCallback(async (currentUser: User) => {
    if (!currentUser?.id) {
      return currentUser;
    }

    try {
      const { data: profile, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', currentUser.id)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!profile) {
        // Auth session is valid but no users row found (RLS may be blocking
        // the read, or the row was never created). Continue with the stored
        // user rather than forcing a logout — an aggressive logout here
        // creates an unrecoverable login loop for affected users.
        console.warn('User profile not found in DB; continuing with stored session.');
        return currentUser;
      }

      const normalizedUser = normalizeAuthUserRow(profile);
      const nextUser = mergeWorkspaceFields(
        normalizedUser,
        getUserActiveWorkspaceId(normalizedUser),
      );

      // Publish only a real change. This runs on every TOKEN_REFRESHED, which
      // is a routine credential rotation rather than a different user, and a
      // brand-new object each time re-rendered every useAuth consumer over an
      // identical profile — churn that is visible as a takeoff drawing
      // reloading under the estimator (#927). Handing back the caller's own
      // copy is the same profile by definition.
      if (sameAuthUser(
        currentUser as unknown as Record<string, unknown>,
        nextUser as unknown as Record<string, unknown>,
      )) {
        return currentUser;
      }

      setUser(nextUser);
      persistAuthUser(nextUser);
      return nextUser;
    } catch (error) {
      console.error('Error hydrating user from backend:', error);
      return currentUser;
    }
  }, [persistAuthUser]);

  useEffect(() => {
    let cancelled = false;
    let recheckTimer: number | null = null;
    let nativeWaitTimer: number | null = null;
    let transientAttempt = 0;
    // Coming back online only matters while a session kept through a
    // transient failure is waiting to be re-checked; otherwise every
    // reconnect would re-run the whole boot (hydrate, device-token and
    // native session publishes) for a session that is already known good.
    const onOnline = () => {
      if (recheckTimer === null) return;
      window.clearTimeout(recheckTimer);
      recheckTimer = null;
      void validateSession();
    };

    // Boot runs on every update reload. See sessionBootstrapPolicy for why a
    // failed check here must not sign anyone out unless the refresh token was
    // definitively rejected.
    const scheduleTransientRecheck = () => {
      const delay = TRANSIENT_RECHECK_DELAYS_MS[transientAttempt];
      transientAttempt += 1;
      if (delay === undefined) return; // API calls handle it from here (client.ts)
      recheckTimer = window.setTimeout(() => {
        recheckTimer = null;
        void validateSession();
      }, delay);
    };

    const validateSession = async () => {
      const currentUser = userRef.current;
      if (!currentUser) {
        setStatus('unauthenticated');
        return;
      }

      // Native shell: the shell owns the refresh-token family. An expired web
      // copy must not be refreshed here (that is token reuse, and Supabase
      // revokes the family on BOTH sides). The boot pull below asks the shell
      // for its pair; establishFromNativeSession flips status when it lands.
      // If the shell never answers, show the login screen but clear nothing.
      if (NativeBridge.isRunningNatively() && !isStoredSessionLive(peekStoredSession(), Date.now())) {
        nativeWaitTimer = window.setTimeout(() => {
          nativeWaitTimer = null;
          if (!cancelled) setStatus(prev => (prev === 'unknown' ? 'unauthenticated' : prev));
        }, NATIVE_SESSION_BOOT_WAIT_MS);
        return;
      }

      let session: any = null;
      let sessionError: unknown = null;
      let timedOut = false;
      try {
        const result = await getSessionWithTimeout();
        session = result.data.session;
        sessionError = result.error;
      } catch (error) {
        timedOut = true;
        sessionError = error;
      }
      // A re-check can be in flight when the user signs out; finishing it
      // would flip status back to authenticated over a signed-out app.
      if (cancelled || !userRef.current) return;

      const verdict = classifyBootSession({
        session,
        error: sessionError,
        timedOut,
        isDeadSessionError,
      });

      if (verdict === 'transient') {
        // Network blip, suspended webview or slow auth server — typical of an
        // update reload fired while the app was backgrounded. The stored
        // refresh token is still good: stay signed in on the saved user and
        // check again shortly. If it later proves dead, API calls sign out.
        console.warn('Session check at startup failed transiently; keeping the session.', sessionError);
        setStatus('authenticated');
        scheduleTransientRecheck();
        return;
      }

      if (verdict === 'absent' || verdict === 'dead') {
        console.warn(`Supabase session ${verdict} on mount — logging out.`);
        setStatus('unauthenticated');
        logout();
        return;
      }

      transientAttempt = 0;

      // A restored aal1 session on an account with an authenticator app (one
      // added on another device, or a tab closed at the code step) must not
      // open the app. Drop the saved app user but keep the Supabase session:
      // the login page picks it up and asks for the code.
      const pendingMfa = await pendingMfaForSession(supabase, session).catch(() => null);
      if (cancelled || !userRef.current) return;
      if (pendingMfa) {
        _setUser(null);
        persistAuthUser(null);
        setStatus('unauthenticated');
        return;
      }

      const hydratedUser = await hydrateUserFromBackend(currentUser);
      if (cancelled || !userRef.current) return;

      DeviceTokenBridge.registerOnLogin(hydratedUser.id, hydratedUser.name || 'User')
        .catch(() => { /* non-critical */ });

      // Publish E2E public key on session restore (fire-and-forget)
      e2ePublishPublicKey(hydratedUser.id).catch(() => {});

      // Notify native app of current auth state
      NativeBridge.setAuthenticated(true);
      const nativeSessionPayload = buildNativeAuthSessionPayload(session);
      if (nativeSessionPayload) {
        NativeBridge.send('authSession', nativeSessionPayload);
      }
      // Cold-load into an authenticated macOS shell: publish tokens so the
      // native layer can establish its own Supabase client immediately.
      publishMacAuthSession(session);
      setStatus('authenticated');
    };

    window.addEventListener('online', onOnline);
    validateSession();

    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
      if (recheckTimer !== null) window.clearTimeout(recheckTimer);
      if (nativeWaitTimer !== null) window.clearTimeout(nativeWaitTimer);
    };
  }, [hydrateUserFromBackend, logout, persistAuthUser]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      rememberWorkspaceAccessToken(session?.access_token);
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        setStatus('authenticated');
        NativeBridge.setAuthenticated(true);
        const nativeSessionPayload = buildNativeAuthSessionPayload(session);
        if (nativeSessionPayload) {
          NativeBridge.send('authSession', nativeSessionPayload);
        }
        // Publish fresh tokens to the macOS native Supabase client on every
        // session event where a valid session exists (SIGNED_IN, TOKEN_REFRESHED,
        // USER_UPDATED). No-op outside the macOS shell.
        publishMacAuthSession(session);

        // Demo session detection. SIGNED_IN fires the moment setSession()
        // resolves — before AuthContext's own login() runs — so userRef is
        // still stale here. Read the email straight off the Supabase
        // session and short-circuit before anything reaches the network.
        const sessionEmail = session?.user?.email;
        const sessionIsDemo = isDemoUser({ email: sessionEmail });
        if (sessionIsDemo) {
          const stub: any = userRef.current && isDemoUser(userRef.current)
            ? userRef.current
            : {
                id: session?.user?.id || 'demo-user',
                email: sessionEmail,
                name: (session?.user?.user_metadata as any)?.name || 'Demo User',
              };
          DataService.enterDemoMode(patchDemoUserCompany(stub));
          return;
        }

        const syncPlan = planAuthEventSync(event);

        const currentUser = userRef.current;
        if (syncPlan.hydrateUser && currentUser?.id) {
          hydrateUserFromBackend(currentUser).catch((error) => {
            console.error('Failed to hydrate user after auth change:', error);
          });
        }

        // A routine token rotation stops here. See authEventSyncPolicy for why
        // the rebind and the convergence are both redundant on TOKEN_REFRESHED,
        // and for what a drawing reloading under the user had to do with it
        // (#927).
        if (!syncPlan.resyncData) return;

        DataService.restartRealtimeSubscriptions()
          .then(() => new Promise<void>((resolve) => {
            // DataService has already hydrated synchronously from its local
            // snapshot. Defer the expensive full /database convergence until
            // the active route has painted; realtime subscriptions are armed
            // immediately above so live changes are not missed.
            const w = window as typeof window & {
              requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
            };
            const refresh = () => {
              void DataService.refreshFromBackend()
                .catch((error: unknown) => console.error('Failed to refresh backend data:', error))
                .finally(resolve);
            };
            // requestIdleCallback can fire before first content on a quiet
            // main thread, so enforce a small grace period as well as waiting
            // for idle. The local snapshot and realtime channel are already
            // available during this delay.
            setTimeout(() => {
              if (w.requestIdleCallback) w.requestIdleCallback(refresh, { timeout: 800 });
              else refresh();
            }, 700);
          }))
          .catch((error: unknown) => {
            console.error('Failed to restart realtime subscriptions after auth change:', error);
          });
        return;
      }

      if (event === 'SIGNED_OUT' || (event as string) === 'USER_DELETED') {
        // Session is already gone — just clear local state.
        // Do NOT call logout() here: logout() calls supabase.auth.signOut()
        // which fires another SIGNED_OUT event, creating an infinite loop.
        if (userRef.current) {
          void preserveWorkspaceLock().finally(() => {
            clearLocalAuthState();
            if (typeof window !== 'undefined') window.location.replace('/');
          });
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [hydrateUserFromBackend, logout]);

  // Native web shell (iOS and macOS): the native app owns sign-in (+ Face ID)
  // and injects the live Supabase session into this webview, so the user is
  // never asked to log in again inside the shell. setSession() establishes
  // the web session and fires SIGNED_IN, but with no stored user it cannot
  // hydrate the app user (it only hydrates an existing userRef), so we
  // hydrate here from the session user id. This also doubles as the landing
  // spot for client.ts's dead-session rescue: when getAuthHeaderToken() asks
  // the shell for a fresh session via 'auth:requestSession', the shell
  // answers with this same 'quantis:native:sessionInjected' event.
  useEffect(() => {
    // Register UNCONDITIONALLY. The handler only does work when a native session
    // is actually injected (the quantis:native:sessionInjected event fires or the
    // __QUANTIS_NATIVE_SESSION__ global is set), which only happens inside a
    // native shell — so it's a no-op in a plain browser tab. Gating on
    // getPlatform()==='ios' at mount was fragile: if this effect ran before the
    // document-start bootstrap set the platform attribute, it bailed permanently
    // and the bridge never armed. Listening for the event removes that race, and
    // not gating by platform means it also covers macOS.
    const establishFromNativeSession = async (accessToken?: string, refreshToken?: string) => {
      if (!accessToken || !refreshToken) return;
      try {
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (error || !data.session?.user?.id) {
          if (error) console.error('Native session injection failed:', error);
          return;
        }
        // A fresh, working session landed — clear client.ts's "session is
        // dead" latch so the next getAuthHeaders() call doesn't keep acting
        // on the stale verdict from before this injection.
        resetSessionExpiredLatch();
        // Only build the app user once; later injections are token refreshes,
        // where setSession() updating the tokens is all that is needed.
        if (!nativeUserEstablishedRef.current) {
          nativeUserEstablishedRef.current = true;
          const supaUser = data.session.user;
          const hydrated = await hydrateUserFromBackend({ id: supaUser.id, email: supaUser.email ?? undefined } as User);
          // App.tsx gates the app on BOTH user AND status. hydrateUserFromBackend
          // only calls setUser when the profile row is readable, so set the user
          // here (the real row or the {id,email} stub it returns) before flipping
          // status — otherwise the shell sits on the web login with
          // status=authenticated but user=null.
          setUser(hydrated);
          setStatus('authenticated');
        }
      } catch (e) {
        console.error('Native session injection error:', e);
      }
    };

    // The native side may set the global before this listener mounts; check it
    // first, then listen for subsequent injections (initial load + refresh).
    const pending = (window as unknown as { __QUANTIS_NATIVE_SESSION__?: { access_token?: string; refresh_token?: string } }).__QUANTIS_NATIVE_SESSION__;
    if (pending?.access_token) {
      void establishFromNativeSession(pending.access_token, pending.refresh_token);
    }

    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      void establishFromNativeSession(detail.access_token, detail.refresh_token);
    };
    window.addEventListener('quantis:native:sessionInjected', handler);

    // PULL at boot, not just push from native. The shell's cold-launch push
    // loop is bounded (~10s) and every pre-commit evaluate lands on a document
    // that is then thrown away — so a page that takes longer than the budget
    // to boot (cold dev server, slow network) got NO injection, ever, and sat
    // on the web login form while the device was signed in natively. Asking
    // from here inverts the race: the request goes out when THIS side is
    // provably ready, and the answer arrives on the listener registered just
    // above. No-op outside the shells (optional chaining) and answered on both
    // platforms now — iOS registers auth:requestSession too.
    //
    // Gated on the web having NO session of its own: injecting the native pair
    // over a live web session replaces it needlessly, and repeated setSession
    // of a shared refresh token is the rotation-family hazard.
    void (async () => {
      if (pending?.access_token) return;
      // A STORED session is not a LIVE one, so gate on expiry: an unexpired
      // access token is presumptively alive, an expired one has nothing to
      // lose by asking. Read storage directly rather than via getSession():
      // getSession() REFRESHES an expired copy, which inside the shell is
      // refresh-token reuse and revokes the family on both sides.
      if (isStoredSessionLive(peekStoredSession(), Date.now())) return;
      (window as unknown as { QuantisNativeBridge?: { send?: (t: string) => void } })
        .QuantisNativeBridge?.send?.('auth:requestSession');
    })();

    return () => window.removeEventListener('quantis:native:sessionInjected', handler);
  }, [hydrateUserFromBackend]);

  useEffect(() => {
    if (!user?.id) {
      setWorkspaceMemberships([]);
      setAvailableWorkspaces([]);
      setActiveWorkspaceId(null);
      return;
    }

    let cancelled = false;

    const run = async () => {
      await loadWorkspaceContext(user);
      if (cancelled) return;
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [user?.id, loadWorkspaceContext]);

  useEffect(() => {
    const companyId = activeWorkspace?.companyId || getUserCompanyId(user);
    if (!companyId) {
      setCompany(null);
      return;
    }

    try {
      const resolvedCompany = DataService.getCompany(companyId);
      setCompany(resolvedCompany || null);
    } catch (error) {
      console.error('Error fetching company:', error);
      setCompany(null);
    }
  }, [user, activeWorkspace]);

  // The caller's role on the active workspace, membership-first — resolved
  // once here so canAccess/permissions don't each re-scan workspaceMemberships.
  const activeMembershipRole = useMemo<WorkspaceRole | null>(() => {
    if (!activeWorkspaceId) return null;
    return workspaceMemberships.find(m => m.workspaceId === activeWorkspaceId)?.workspaceRole ?? null;
  }, [workspaceMemberships, activeWorkspaceId]);

  const canAccess = useCallback((permission: Permission): boolean => {
    return user ? canUserAccess(user, permission, activeMembershipRole) : false;
  }, [user, activeMembershipRole]);

  const canViewCompany = useCallback((companyId: string): boolean => {
    return user ? canUserViewCompany(user, companyId) : false;
  }, [user]);

  // Legacy branches cover cached sessions still carrying the old
  // role values (admin / staff / client / supervisor).
  const isAdmin = useCallback((): boolean => {
    const r = user?.role;
    return r === 'super_admin' || r === 'internal' || r === 'admin' || r === 'staff' || false;
  }, [user]);

  const isStaff = useCallback((): boolean => {
    const r = user?.role;
    return r === 'internal' || r === 'staff' || false;
  }, [user]);

  // "workspace-tier person" — true for owner/admin/user alike (see
  // ClientDashboardNew.tsx header comment: covers all workspace roles,
  // not just the legacy 'client' string). Membership-first: any active
  // membership row on the active workspace settles it; the legacy
  // user.role list stays verbatim as the fallback for accounts that
  // predate a workspace_memberships row.
  const isClient = useCallback((): boolean => {
    if (!user) return false;
    // Platform staff can HOLD memberships (e.g. owning their own workspace)
    // without being workspace-tier people — the membership arm must not
    // reclassify them or internal users lose the cross-workspace dashboard.
    if (isInternalRole(user.role)) return false;
    const wsId = activeWorkspaceId ?? null;
    if (wsId && workspaceMemberships.some(m => m.workspaceId === wsId)) {
      return true;
    }
    const r = user.role;
    return r === 'workspace_owner' || r === 'workspace_admin' || r === 'workspace_user'
        || r === 'member'          || r === 'client'
        || false;
  }, [user, workspaceMemberships, activeWorkspaceId]);

  // Site-team baseline. Post-rename: workspace_user is the canonical
  // home for the gates the legacy supervisor role used to own.
  // Membership-first: only the workspace_user tier on the active
  // workspace qualifies (owner/admin fall through to isClient's
  // dashboard branch) — legacy user.role list stays verbatim as fallback.
  const isSupervisor = useCallback((): boolean => {
    if (!user) return false;
    if (isInternalRole(user.role)) return false; // see isClient
    const wsId = activeWorkspaceId ?? null;
    if (wsId && workspaceMemberships.some(m => m.workspaceId === wsId && m.workspaceRole === 'workspace_user')) {
      return true;
    }
    const r = user.role;
    return r === 'workspace_user' || r === 'supervisor' || false;
  }, [user, workspaceMemberships, activeWorkspaceId]);

  const isSuperAdmin = useCallback((): boolean => {
    // Delegates so prop-driven screens (FinanceBilling) and the context cannot
    // drift on what "administrator" means. Note the name is historical — this
    // admits `admin` as well as `super_admin`.
    return isAdministratorRole(user?.role);
  }, [user]);

  const isInternal = useCallback((): boolean => {
    const r = user?.role;
    return r === 'super_admin' || r === 'internal' || false;
  }, [user]);

  // "Member" of a workspace at any tier. Post-rename: workspace_owner /
  // workspace_admin / workspace_user are all members. `member` (legacy
  // alias) is the workspace_owner equivalent. Membership-first, same
  // any-tier rule as isClient; legacy user.role list stays verbatim
  // as the fallback arm.
  const isMember = useCallback((): boolean => {
    if (!user) return false;
    if (isInternalRole(user.role)) return false; // see isClient
    const wsId = activeWorkspaceId ?? null;
    if (wsId && workspaceMemberships.some(m => m.workspaceId === wsId)) {
      return true;
    }
    const r = user.role;
    return r === 'workspace_owner' || r === 'workspace_admin' || r === 'workspace_user'
        || r === 'member'          || r === 'client'          || false;
  }, [user, workspaceMemberships, activeWorkspaceId]);

  // internal / super_admin always pass — consultant pattern.
  const isWorkspaceOwner = useCallback((workspaceId?: string): boolean => {
    if (!user) return false;
    const r = user.role;
    if (r === 'super_admin' || r === 'internal') return true;
    const wsId = workspaceId ?? activeWorkspaceId ?? null;
    if (!wsId) return false;
    return workspaceMemberships.some(
      m => m.workspaceId === wsId && m.workspaceRole === 'workspace_owner',
    );
  }, [user, workspaceMemberships, activeWorkspaceId]);

  const isWorkspaceAdmin = useCallback((workspaceId?: string): boolean => {
    if (!user) return false;
    const r = user.role;
    if (r === 'super_admin' || r === 'internal') return true;
    const wsId = workspaceId ?? activeWorkspaceId ?? null;
    if (!wsId) return false;
    return workspaceMemberships.some(
      m => m.workspaceId === wsId &&
           (m.workspaceRole === 'workspace_admin' || m.workspaceRole === 'workspace_owner'),
    );
  }, [user, workspaceMemberships, activeWorkspaceId]);

  const switchWorkspace = useCallback(async (workspaceId: string) => {
    if (!user) return;
    if (!availableWorkspaces.some(workspace => workspace.id === workspaceId)) return;
    if (workspaceId === activeWorkspaceId) return;

    const previousWorkspaceId = activeWorkspaceId;
    const nextUser = mergeWorkspaceFields(user, workspaceId);

    setActiveWorkspaceId(workspaceId);
    setUser(nextUser);
    persistAuthUser(nextUser);

    try {
      const { error } = await supabase
        .from('users')
        .update({ active_workspace_id: workspaceId })
        .eq('id', user.id);

      if (error) {
        throw error;
      }

      await DataService.refreshFromBackend();
    } catch (error) {
      console.error('Failed to switch active workspace:', error);
      const revertedUser = mergeWorkspaceFields(user, previousWorkspaceId);
      setActiveWorkspaceId(previousWorkspaceId);
      setUser(revertedUser);
      persistAuthUser(revertedUser);
      throw error;
    }
  }, [activeWorkspaceId, availableWorkspaces, persistAuthUser, user]);

  const login = useCallback((userData: User, _rememberMe: boolean = false) => {
    // Demo users get a synthesised dataset instead of Supabase data, and
    // never make a network round-trip. Patch the user with the demo
    // company id so AuthContext's company lookup resolves to the fixture.
    const demo = isDemoUser(userData);
    const seedUser = demo ? patchDemoUserCompany(userData as any) as any as User : userData; // TODO: tighten — auth.User vs database.User split
    const nextUser = mergeWorkspaceFields(seedUser as any, getUserActiveWorkspaceId(seedUser)) as User;
    if (demo) {
      DataService.enterDemoMode(nextUser as any); // TODO: tighten type — auth.User vs database.User split
    } else {
      DataService.exitDemoMode();
    }
    setUser(nextUser);
    setActiveWorkspaceId(getUserActiveWorkspaceId(nextUser));
    setWorkspaceMemberships([]);
    setAvailableWorkspaces([]);
    persistAuthUser(nextUser);

    // NOTE: DataService.restartRealtimeSubscriptions() + refreshFromBackend() are
    // intentionally NOT called here. They are already triggered by the
    // onAuthStateChange SIGNED_IN handler which fires before onLogin() is called
    // (from supabase.auth.setSession). Calling them again here would cause a
    // double-initialization that tears down and rebuilds all subscriptions twice,
    // flooding the component tree with state updates on every login.

    DeviceTokenBridge.registerOnLogin(nextUser.id, nextUser.name || 'User')
      .then((registered) => {
        if (registered) console.log('[Auth] APNs device token auto-registered on login');
      })
      .catch((err) => {
        console.warn('[Auth] Device token registration failed (non-critical):', err);
      });

    // Publish E2E public key (fire-and-forget -- non-critical)
    e2ePublishPublicKey(nextUser.id).catch(() => {});

    // Notify native app that user is authenticated
    NativeBridge.setAuthenticated(true);
    setStatus('authenticated');
  }, [persistAuthUser]);

  const updateUser = useCallback((updatedUser: User) => {
    const nextUser = mergeWorkspaceFields(updatedUser, getUserActiveWorkspaceId(updatedUser));
    setUser(nextUser);
    setActiveWorkspaceId(getUserActiveWorkspaceId(nextUser));
    persistAuthUser(nextUser);
  }, [persistAuthUser]);

  const permissions = useMemo(
    () => user ? getUserPermissions(user, activeMembershipRole) : [],
    [user, activeMembershipRole],
  );

  const value = useMemo<AuthContextType>(() => ({
    user,
    status,
    company,
    activeWorkspaceId,
    activeWorkspace,
    availableWorkspaces,
    workspaceMemberships,
    permissions,
    canAccess,
    canViewCompany,
    isAdmin,
    isStaff,
    isClient,
    isSupervisor,
    isSuperAdmin,
    isInternal,
    isMember,
    isWorkspaceOwner,
    isWorkspaceAdmin,
    switchWorkspace,
    login,
    logout,
    updateUser,
  }), [
    user,
    status,
    company,
    activeWorkspaceId,
    activeWorkspace,
    availableWorkspaces,
    workspaceMemberships,
    permissions,
    canAccess,
    canViewCompany,
    isAdmin,
    isStaff,
    isClient,
    isSupervisor,
    isSuperAdmin,
    isInternal,
    isMember,
    isWorkspaceOwner,
    isWorkspaceAdmin,
    switchWorkspace,
    login,
    logout,
    updateUser,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
